import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EMISSION_CLASSES,
  ENERGY_SOURCES,
  VEHICLE_CATEGORIES,
  categoryGroupOf,
  energySourceDefinition,
  evaluate,
  explainRule,
  getCleanVehicleRule,
  normalizePlate,
  vehicleFactsOf,
  type CreateVehicle,
  type EmissionClass,
  type EnergySource,
  type Party,
  type Vehicle,
  type VehicleCategory,
} from '@dashboard/contracts';
import { useCreateVehicle, useDeleteVehicle, useUpdateVehicle } from '../../api/queries';
import { ModalCard } from '../ui/modal-dialog';
import { useConfirm } from '../ui/confirm-dialog';
import { MutationError } from '../platform/form-helpers';
import { energyLabelKey, ruleSentence } from './fleet-labels';

/**
 * Adding a vehicle, and correcting one.
 *
 * One dialog for both, because the fields are identical and the difference is
 * three lines: which mutation runs, whether a version goes with it, and what
 * the heading says. Two components would be two places to add the next field
 * to, and the register's own creator/editor split is a warning rather than a
 * precedent -- it exists there because a creator is mounted in three other
 * surfaces, which this is not.
 *
 * ## The verdict is live
 *
 * The panel at the bottom re-judges the vehicle on every keystroke, from the
 * same catalog the API and the report read (`evaluate` in
 * `@dashboard/contracts`). That is the whole reason the rules live in the
 * contracts package rather than behind the API.
 *
 * It matters most when it says *nothing*. A missing CO₂ figure makes a vehicle
 * uncountable, and somebody who learns that while the form is open goes and
 * finds the number; somebody who learns it six months later from a municipal
 * officer's email does not.
 *
 * The date it judges on is the second reference period's start, not today,
 * because that is the question a person entering a vehicle in 2026 actually has
 * -- and the control lets them check the other one.
 */

export interface VehicleDraft {
  registrationPlate: string;
  vin: string;
  category: VehicleCategory;
  energySources: EnergySource[];
  emissionClass: EmissionClass | '';
  co2WltpGramsPerKm: string;
  co2GramsPerKwh: string;
  maxRdeNoxPercent: string;
  firstRegisteredOn: string;
  withdrawnOn: string;
  partyId: string;
  notes: string;
}

export function emptyVehicleDraft(): VehicleDraft {
  return {
    registrationPlate: '',
    vin: '',
    category: 'M3',
    energySources: [],
    emissionClass: '',
    co2WltpGramsPerKm: '',
    co2GramsPerKwh: '',
    maxRdeNoxPercent: '',
    firstRegisteredOn: '',
    withdrawnOn: '',
    partyId: '',
    notes: '',
  };
}

export function draftFromVehicle(vehicle: Vehicle): VehicleDraft {
  return {
    registrationPlate: vehicle.registrationPlate,
    vin: vehicle.vin ?? '',
    category: vehicle.category,
    energySources: [...vehicle.energySources],
    emissionClass: vehicle.emissionClass ?? '',
    co2WltpGramsPerKm: vehicle.co2WltpGramsPerKm?.toString() ?? '',
    co2GramsPerKwh: vehicle.co2GramsPerKwh?.toString() ?? '',
    maxRdeNoxPercent: vehicle.maxRdeNoxPercent?.toString() ?? '',
    firstRegisteredOn: vehicle.firstRegisteredOn ?? '',
    withdrawnOn: vehicle.withdrawnOn ?? '',
    partyId: vehicle.party?.id ?? '',
    notes: vehicle.notes ?? '',
  };
}

/**
 * The numeric fields hold text, not numbers.
 *
 * A number input bound to parsed state re-parses on every keystroke, and "1"
 * on the way to "138" is a different value than the one being typed towards.
 * Parsing on submit lets the intermediate states exist -- the same rule the map
 * defaults form follows for coordinates.
 */
function numberOrNull(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function draftToWrite(draft: VehicleDraft): CreateVehicle | null {
  const co2 = numberOrNull(draft.co2WltpGramsPerKm);
  const kwh = numberOrNull(draft.co2GramsPerKwh);
  const rde = numberOrNull(draft.maxRdeNoxPercent);
  if (co2 === undefined || kwh === undefined || rde === undefined) return null;
  if (draft.registrationPlate.trim() === '' || draft.energySources.length === 0) return null;

  return {
    registrationPlate: draft.registrationPlate.trim(),
    vin: draft.vin.trim() === '' ? null : draft.vin.trim().toUpperCase(),
    category: draft.category,
    energySources: draft.energySources,
    emissionClass: draft.emissionClass === '' ? null : draft.emissionClass,
    co2WltpGramsPerKm: co2,
    co2GramsPerKwh: kwh,
    maxRdeNoxPercent: rde,
    firstRegisteredOn: draft.firstRegisteredOn === '' ? null : draft.firstRegisteredOn,
    withdrawnOn: draft.withdrawnOn === '' ? null : draft.withdrawnOn,
    partyId: draft.partyId === '' ? null : draft.partyId,
    notes: draft.notes.trim() === '' ? null : draft.notes.trim(),
  };
}

/** Which figures the rules will actually read for this category, so the form
 *  can mark them required-ish rather than presenting nine equal boxes. */
function relevantFigures(category: VehicleCategory): {
  co2: boolean;
  rde: boolean;
  kwh: boolean;
} {
  const heavy = categoryGroupOf(category) !== 'light_duty';
  return { co2: !heavy, rde: !heavy, kwh: heavy };
}

export function VehicleDialog({
  vehicle,
  parties,
  operatorFixed = null,
  onClose,
}: {
  /** Null to add one. */
  vehicle: Vehicle | null;
  parties: Party[];
  /**
   * The operator, when it is not the reader's to choose: a supplier's vehicles
   * are always their own organization's, and the API records them so whatever
   * is sent (docs/organizations-design.md section 3.3).
   */
  operatorFixed?: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const baseId = useId();
  const create = useCreateVehicle();
  const update = useUpdateVehicle();
  const remove = useDeleteVehicle();
  const { ask, confirmDialog } = useConfirm();

  const [draft, setDraft] = useState<VehicleDraft>(() =>
    vehicle ? draftFromVehicle(vehicle) : emptyVehicleDraft(),
  );
  /* The second reference period by default: an operator entering a vehicle
     today is being procured against the rules that apply now, and the first
     period is history they can still check. */
  const [judgeOn, setJudgeOn] = useState('2026-01-01');

  const patch = (values: Partial<VehicleDraft>) =>
    setDraft((current) => ({ ...current, ...values }));

  const write = draftToWrite(draft);
  const pending = create.isPending || update.isPending || remove.isPending;
  const figures = relevantFigures(draft.category);

  const verdict = evaluate(
    vehicleFactsOf({
      category: draft.category,
      energySources: draft.energySources,
      emissionClass: draft.emissionClass === '' ? null : draft.emissionClass,
      co2WltpGramsPerKm: numberOrNull(draft.co2WltpGramsPerKm) ?? null,
      co2GramsPerKwh: numberOrNull(draft.co2GramsPerKwh) ?? null,
      maxRdeNoxPercent: numberOrNull(draft.maxRdeNoxPercent) ?? null,
    }),
    judgeOn,
  );

  /*
   * The rule that produced the verdict above, reached by the id the evaluation
   * itself reports rather than looked up again from the category and the date.
   *
   * The panel used to say what the answer was and never what asked the
   * question, which left the most common reaction to it -- "why?" -- with
   * nowhere to go from inside a modal. Folded away, because somebody entering
   * their fortieth van does not need to be told the rule forty times.
   */
  const rule = verdict.ruleId === null ? null : getCleanVehicleRule(verdict.ruleId);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!write || pending) return;

    if (vehicle) {
      update.mutate({ id: vehicle.id, ...write, version: vehicle.version }, { onSuccess: onClose });
    } else {
      create.mutate(write, { onSuccess: onClose });
    }
  };

  /**
   * Deleting, which the API refuses once the vehicle has been declared.
   *
   * The control is offered anyway rather than hidden on `assignmentCount`: a
   * refusal that explains itself ("declared on an agreement -- mark it
   * withdrawn from service instead") teaches the rule, and a button that
   * silently is not there teaches nothing. `MutationError` renders it.
   */
  const handleDelete = async () => {
    if (!vehicle) return;
    const confirmed = await ask({
      message: t('fleet.vehicleForm.confirmDelete', { plate: vehicle.registrationPlate }),
      confirmLabel: t('editor.delete'),
      destructive: true,
    });
    if (confirmed) remove.mutate(vehicle.id, { onSuccess: onClose });
  };

  const toggleSource = (source: EnergySource) =>
    patch({
      energySources: draft.energySources.includes(source)
        ? draft.energySources.filter((entry) => entry !== source)
        : [...draft.energySources, source],
    });

  return (
    <ModalCard
      titleId={titleId}
      title={vehicle ? t('fleet.vehicleForm.editTitle') : t('fleet.vehicleForm.addTitle')}
      onClose={onClose}
      panelClassName="modal__panel--narrow-form"
    >
      <form className="fleet-form" onSubmit={submit}>
        <label className="editor-field" htmlFor={`${baseId}-plate`}>
          <span>{t('fleet.vehicles.plate')}</span>
          <input
            id={`${baseId}-plate`}
            className="input"
            value={draft.registrationPlate}
            maxLength={16}
            autoFocus
            required
            onChange={(event) => patch({ registrationPlate: event.target.value })}
          />
          {/* The normalized form, shown rather than explained: it is what the
              duplicate report groups by, and seeing "ABC123" appear as somebody
              types "abc-123" is a better explanation than a sentence. */}
          {draft.registrationPlate.trim() !== '' ? (
            <span className="cell-secondary">
              {t('fleet.vehicleForm.normalizedAs', {
                plate: normalizePlate(draft.registrationPlate),
              })}
            </span>
          ) : null}
        </label>

        <label className="editor-field" htmlFor={`${baseId}-category`}>
          <span>{t('fleet.vehicles.category')}</span>
          <select
            id={`${baseId}-category`}
            className="select"
            value={draft.category}
            onChange={(event) => patch({ category: event.target.value as VehicleCategory })}
          >
            {VEHICLE_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category} — {t(`fleet.categoryName.${category}`)}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="editor-field editor-field--group">
          <legend>{t('fleet.vehicles.energy')}</legend>
          {/* Checkboxes, not a select: a plug-in hybrid genuinely has two, and
              a single-choice control would make somebody pick one and lose the
              half that decides the answer. */}
          <div className="fleet-form__checks">
            {ENERGY_SOURCES.map((source) => (
              <label key={source} className="fleet-form__check">
                <input
                  type="checkbox"
                  checked={draft.energySources.includes(source)}
                  onChange={() => toggleSource(source)}
                />
                <span>{t(energyLabelKey(source))}</span>
                {energySourceDefinition(source).zeroEmission ? (
                  <span className="status-badge status-badge--success">
                    {t('fleet.vehicleForm.zeroEmission')}
                  </span>
                ) : null}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="fleet-form__row">
          {figures.co2 ? (
            <label className="editor-field" htmlFor={`${baseId}-co2`}>
              <span>{t('fleet.vehicleForm.co2Wltp')}</span>
              <input
                id={`${baseId}-co2`}
                className="input"
                inputMode="numeric"
                value={draft.co2WltpGramsPerKm}
                onChange={(event) => patch({ co2WltpGramsPerKm: event.target.value })}
              />
            </label>
          ) : null}

          {figures.rde ? (
            <label className="editor-field" htmlFor={`${baseId}-rde`}>
              <span>{t('fleet.vehicleForm.rde')}</span>
              <input
                id={`${baseId}-rde`}
                className="input"
                inputMode="numeric"
                value={draft.maxRdeNoxPercent}
                onChange={(event) => patch({ maxRdeNoxPercent: event.target.value })}
              />
            </label>
          ) : null}

          {figures.kwh ? (
            <label className="editor-field" htmlFor={`${baseId}-kwh`}>
              <span>{t('fleet.vehicleForm.co2Kwh')}</span>
              <input
                id={`${baseId}-kwh`}
                className="input"
                inputMode="numeric"
                value={draft.co2GramsPerKwh}
                onChange={(event) => patch({ co2GramsPerKwh: event.target.value })}
              />
            </label>
          ) : null}
        </div>

        <div className="fleet-form__row">
          <label className="editor-field" htmlFor={`${baseId}-euro`}>
            <span>{t('fleet.vehicleForm.emissionClass')}</span>
            <select
              id={`${baseId}-euro`}
              className="select"
              value={draft.emissionClass}
              onChange={(event) =>
                patch({ emissionClass: event.target.value as EmissionClass | '' })
              }
            >
              <option value="">{t('fleet.vehicleForm.notRecorded')}</option>
              {EMISSION_CLASSES.map((value) => (
                <option key={value} value={value}>
                  {t(`fleet.emissionClass.${value}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="editor-field" htmlFor={`${baseId}-vin`}>
            <span>{t('fleet.vehicleForm.vin')}</span>
            <input
              id={`${baseId}-vin`}
              className="input"
              value={draft.vin}
              maxLength={17}
              onChange={(event) => patch({ vin: event.target.value })}
            />
          </label>
        </div>

        <div className="fleet-form__row">
          <label className="editor-field" htmlFor={`${baseId}-registered`}>
            <span>{t('fleet.vehicleForm.firstRegistered')}</span>
            <input
              id={`${baseId}-registered`}
              className="input"
              type="date"
              value={draft.firstRegisteredOn}
              onChange={(event) => patch({ firstRegisteredOn: event.target.value })}
            />
          </label>

          <label className="editor-field" htmlFor={`${baseId}-withdrawn`}>
            <span>{t('fleet.vehicleForm.withdrawnOn')}</span>
            <input
              id={`${baseId}-withdrawn`}
              className="input"
              type="date"
              value={draft.withdrawnOn}
              onChange={(event) => patch({ withdrawnOn: event.target.value })}
            />
          </label>
        </div>

        {operatorFixed ? (
          <div className="editor-field">
            <span>{t('fleet.vehicleForm.operator')}</span>
            <strong>{operatorFixed}</strong>
          </div>
        ) : (
          <label className="editor-field" htmlFor={`${baseId}-party`}>
            <span>{t('fleet.vehicleForm.operator')}</span>
            <select
              id={`${baseId}-party`}
              className="select"
              value={draft.partyId}
              onChange={(event) => patch({ partyId: event.target.value })}
            >
              <option value="">{t('fleet.vehicleForm.ownFleet')}</option>
              {parties
                .filter((party) => !party.isHost)
                .map((party) => (
                  <option key={party.id} value={party.id}>
                    {party.name}
                  </option>
                ))}
            </select>
          </label>
        )}

        <label className="editor-field" htmlFor={`${baseId}-notes`}>
          <span>{t('fleet.vehicleForm.notes')}</span>
          <textarea
            id={`${baseId}-notes`}
            className="input"
            rows={2}
            maxLength={2000}
            value={draft.notes}
            onChange={(event) => patch({ notes: event.target.value })}
          />
        </label>

        <section className={`fleet-verdict fleet-verdict--${verdict.verdict}`} aria-live="polite">
          <div className="fleet-verdict__head">
            <strong>{t(`fleet.verdict.${verdict.verdict}`)}</strong>
            <label className="fleet-verdict__date" htmlFor={`${baseId}-judge`}>
              <span>{t('fleet.vehicles.asOf')}</span>
              <input
                id={`${baseId}-judge`}
                className="input"
                type="date"
                value={judgeOn}
                onChange={(event) => setJudgeOn(event.target.value)}
              />
            </label>
          </div>
          <p>{t(verdict.reasonKey)}</p>
          {rule ? (
            <details className="fleet-verdict__rule">
              <summary>{t('fleet.vehicleForm.ruleApplied', { rule: t(rule.labelKey) })}</summary>
              <p className="fleet-verdict__rule-heading">{t('fleet.rules.cleanWhen')}</p>
              <ul>
                {explainRule(rule).cleanWhen.map((requirement) => (
                  <li key={requirement.labelKey}>{ruleSentence(t, requirement)}</li>
                ))}
              </ul>
            </details>
          ) : null}
        </section>

        <MutationError error={create.error ?? update.error ?? remove.error} />

        <div className="form-actions">
          {vehicle ? (
            <button
              type="button"
              className="button button--danger"
              disabled={pending}
              onClick={() => void handleDelete()}
            >
              {t('editor.delete')}
            </button>
          ) : null}
          <button type="button" className="button button--ghost" onClick={onClose}>
            {t('editor.cancel')}
          </button>
          <button type="submit" className="button button--primary" disabled={!write || pending}>
            {pending ? t('editor.saving') : t('editor.save')}
          </button>
        </div>
      </form>
      {confirmDialog}
    </ModalCard>
  );
}
